import { computed, type Ref } from 'vue'
import { useHead } from '@unhead/vue'
import { useRequestURL } from '#app'
import {
    getFragmentData,
    graphql,
    type FragmentType
} from '~/resources/graphql'

export const PlanPresentationSeoFragment = graphql(/** GraphQL */ `
    fragment PlanPresentationSeoFragment on PlanPresentation {
        title
        metaDescription
        shortSummary
        bookingImage
        bookingPageHeader
        bookingPageFooter
        bookingPageSections {
            title
            body
        }
    }
`)

type SeoPlanPresentation = FragmentType<
    typeof PlanPresentationSeoFragment
> | null

export function usePlanPresentationSeo(
    title: Readonly<Ref<string>>,
    presentation: Readonly<Ref<SeoPlanPresentation>>
) {
    const requestUrl = useRequestURL()
    const presentationData = computed(() => {
        return getSeoPlanPresentation(presentation.value)
    })
    const description = computed(() => {
        return getPresentationDescription(presentationData.value)
    })
    const image = computed(() => {
        const imagePath = getPresentationImage(presentationData.value)
        if (imagePath === null) {
            return null
        }

        // Social preview images should be absolute even if a presentation ever
        // stores a path-only image URL.
        return new URL(imagePath, requestUrl.origin).toString()
    })

    useHead({
        meta: computed(() => {
            const meta = [
                {
                    key: 'og:title',
                    property: 'og:title',
                    content: title.value
                },
                {
                    key: 'og:type',
                    property: 'og:type',
                    content: 'website'
                },
                {
                    key: 'twitter:card',
                    name: 'twitter:card',
                    content:
                        image.value !== null ? 'summary_large_image' : 'summary'
                },
                {
                    key: 'twitter:title',
                    name: 'twitter:title',
                    content: title.value
                }
            ]

            if (description.value !== null) {
                meta.push(
                    {
                        key: 'description',
                        name: 'description',
                        content: description.value
                    },
                    {
                        key: 'og:description',
                        property: 'og:description',
                        content: description.value
                    },
                    {
                        key: 'twitter:description',
                        name: 'twitter:description',
                        content: description.value
                    }
                )
            }

            if (image.value !== null) {
                meta.push(
                    {
                        key: 'og:image',
                        property: 'og:image',
                        content: image.value
                    },
                    {
                        key: 'og:image:alt',
                        property: 'og:image:alt',
                        content: title.value
                    },
                    {
                        key: 'twitter:image',
                        name: 'twitter:image',
                        content: image.value
                    },
                    {
                        key: 'twitter:image:alt',
                        name: 'twitter:image:alt',
                        content: title.value
                    }
                )
            }

            return meta
        })
    })
}

function getSeoPlanPresentation(presentation: SeoPlanPresentation) {
    if (presentation === null) {
        return null
    }

    return getFragmentData(PlanPresentationSeoFragment, presentation)
}

type SeoPlanPresentationData = ReturnType<typeof getSeoPlanPresentation>

function getPresentationDescription(presentation: SeoPlanPresentationData) {
    if (presentation === null) {
        return null
    }

    // The dedicated SEO field wins so operators can tune search snippets
    // without changing visible booking page copy.
    const metaDescription = cleanHtmlForMetaDescription(
        presentation.metaDescription ?? ''
    )
    if (metaDescription !== '') {
        return metaDescription
    }

    const summary = cleanHtmlForMetaDescription(presentation.shortSummary ?? '')
    if (summary !== '') {
        return summary
    }

    const content = cleanHtmlForMetaDescription(
        [
            presentation.bookingPageHeader ?? '',
            ...(presentation.bookingPageSections ?? []).map(section => {
                return [section.title, section.body].join(' ')
            }),
            presentation.bookingPageFooter ?? ''
        ].join(' ')
    )

    if (content === '') {
        return null
    }

    return content
}

function getPresentationImage(presentation: SeoPlanPresentationData) {
    if (
        presentation === null ||
        typeof presentation.bookingImage !== 'string' ||
        presentation.bookingImage === ''
    ) {
        return null
    }

    return presentation.bookingImage
}

function cleanHtmlForMetaDescription(html: string) {
    const text = html
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ' ')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/g, "'")
        .replace(/&rsquo;/gi, "'")
        .replace(/&lsquo;/gi, "'")
        .replace(/&rdquo;/gi, '"')
        .replace(/&ldquo;/gi, '"')
        .replace(/&hellip;/gi, '...')
        .replace(/\s+/g, ' ')
        .trim()

    return truncateMetaDescription(text)
}

function truncateMetaDescription(text: string) {
    const maxLength = 160
    if (text.length <= maxLength) {
        return text
    }

    const clipped = text.slice(0, maxLength + 1).trim()
    const lastSpace = clipped.lastIndexOf(' ')
    const truncationPoint = lastSpace > 80 ? lastSpace : maxLength

    return clipped
        .slice(0, truncationPoint)
        .replace(/[.,;:!?-]+$/g, '')
        .trim()
        .concat('...')
}
